if (!customElements.get('cp-scratch-offer')) {
  class CPScratchOffer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      if (this.dataset.enabled !== 'true') return;

      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('.cp-scratch-offer__canvas');
      this.closeButton = this.querySelector('.cp-scratch-offer__close');
      this.claimButton = this.querySelector('.cp-scratch-offer__claim');
      this.emailInput = this.querySelector('.cp-scratch-offer__email');
      this.successState = this.querySelector('[data-cp-scratch-success]');
      this.formErrorState = this.querySelector('[data-cp-scratch-errors]');
      if (!this.dialog || !this.canvas) return;

      this.sectionKey = 'cp-scratch-offer:global';
      this.threshold = Number(this.dataset.threshold || 40) / 100;
      this.triggerMode = this.dataset.trigger || 'delay';
      this.isDrawing = false;
      this.lastPoint = null;

      this.onCloseClick = () => this.closeOffer();
      this.onDialogCancel = () => this.markDismissed();
      this.onPointerDown = (event) => this.startScratch(event);
      this.onPointerMove = (event) => this.moveScratch(event);
      this.onPointerUp = () => this.endScratch();
      this.onKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.reveal();
        }
      };
      this.onExitIntent = (event) => {
        if (event.relatedTarget || event.clientY > 0) return;
        this.clearTrigger();
        this.open();
      };

      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.dialog.addEventListener('cancel', this.onDialogCancel);
      this.canvas.addEventListener('pointerdown', this.onPointerDown);
      this.canvas.addEventListener('pointermove', this.onPointerMove);
      this.canvas.addEventListener('pointerup', this.onPointerUp);
      this.canvas.addEventListener('pointercancel', this.onPointerUp);
      this.canvas.addEventListener('keydown', this.onKeyDown);

      if (this.successState) {
        this.classList.add('is-revealed', 'is-claimed');
        this.markClaimed();
        this.open(true);
        requestAnimationFrame(() => this.successState?.focus({ preventScroll: true }));
        return;
      }

      if (this.formErrorState) {
        this.classList.add('is-revealed');
        this.open(true);
        requestAnimationFrame(() => this.emailInput?.focus({ preventScroll: true }));
        return;
      }

      if (!this.isEligible()) return;
      this.setupTrigger();
    }

    disconnectedCallback() {
      this.clearTrigger();
      this.resizeObserver?.disconnect();
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.dialog?.removeEventListener('cancel', this.onDialogCancel);
      this.canvas?.removeEventListener('pointerdown', this.onPointerDown);
      this.canvas?.removeEventListener('pointermove', this.onPointerMove);
      this.canvas?.removeEventListener('pointerup', this.onPointerUp);
      this.canvas?.removeEventListener('pointercancel', this.onPointerUp);
      this.canvas?.removeEventListener('keydown', this.onKeyDown);
    }

    safeStorage(storage, action, key, value) {
      try {
        if (action === 'get') return storage.getItem(key);
        if (action === 'set') storage.setItem(key, value);
      } catch (error) {
        return null;
      }
      return null;
    }

    deviceAllowed() {
      const mode = this.dataset.device || 'all';
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (mode === 'mobile') return isMobile;
      if (mode === 'desktop') return !isMobile;
      return true;
    }

    isEligible() {
      if (!this.deviceAllowed()) return false;

      if (window.Shopify?.designMode) return true;

      if (this.safeStorage(localStorage, 'get', `${this.sectionKey}:dismissed`)) {
        return false;
      }

      if (this.safeStorage(localStorage, 'get', `${this.sectionKey}:claimed`)) {
        return false;
      }

      const frequency = this.dataset.frequency || 'once';
      if (frequency === 'every_visit') return true;
      if (frequency === 'once') return !this.safeStorage(localStorage, 'get', `${this.sectionKey}:shown`);
      if (frequency === 'session') return !this.safeStorage(sessionStorage, 'get', `${this.sectionKey}:shown`);

      const lastShown = Number(this.safeStorage(localStorage, 'get', `${this.sectionKey}:shown`) || 0);
      if (!lastShown) return true;
      const elapsed = Date.now() - lastShown;
      const limit = frequency === 'week' ? 7 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
      return elapsed >= limit;
    }

    setupTrigger() {
      if (window.Shopify?.designMode) {
        this.openTimer = window.setTimeout(() => this.open(), 250);
        return;
      }

      if (this.triggerMode === 'page_load') {
        this.openTimer = window.setTimeout(() => this.open(), 0);
        return;
      }

      if (this.triggerMode === 'exit_intent') {
        const hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        if (hasFinePointer) {
          document.addEventListener('mouseout', this.onExitIntent);
          return;
        }
      }

      const delay = Math.max(0, Number(this.dataset.delay || 0)) * 1000;
      this.openTimer = window.setTimeout(() => this.open(), delay);
    }

    clearTrigger() {
      if (this.openTimer) {
        window.clearTimeout(this.openTimer);
        this.openTimer = null;
      }
      if (this.onExitIntent) document.removeEventListener('mouseout', this.onExitIntent);
    }

    markShown() {
      if (window.Shopify?.designMode) return;
      const frequency = this.dataset.frequency || 'once';
      if (frequency === 'once') this.safeStorage(localStorage, 'set', `${this.sectionKey}:shown`, '1');
      if (frequency === 'session') this.safeStorage(sessionStorage, 'set', `${this.sectionKey}:shown`, '1');
      if (frequency === 'day' || frequency === 'week') {
        this.safeStorage(localStorage, 'set', `${this.sectionKey}:shown`, String(Date.now()));
      }
    }

    markDismissed() {
      if (window.Shopify?.designMode) return;
      this.safeStorage(localStorage, 'set', `${this.sectionKey}:dismissed`, '1');
    }

    markClaimed() {
      if (window.Shopify?.designMode) return;
      this.safeStorage(localStorage, 'set', `${this.sectionKey}:claimed`, '1');
    }

    closeOffer() {
      this.markDismissed();
      this.dialog.close();
    }

    open(force = false) {
      if (this.dialog.open) return;
      if (!force && !window.Shopify?.designMode && !this.isEligible()) return;
      this.clearTrigger();
      if (!force) this.markShown();
      this.dialog.showModal();
      if (!this.classList.contains('is-claimed')) this.setupCanvas();
      if (!force) this.closeButton?.focus({ preventScroll: true });
    }

    setupCanvas() {
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.paintCover());
      this.resizeObserver.observe(this.canvas);
      this.paintCover();
    }

    paintCover() {
      const rect = this.canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const styles = getComputedStyle(this);
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.fillStyle = styles.getPropertyValue('--cp-scratch-cover').trim() || '#b8c1b8';
      this.ctx.fillRect(0, 0, rect.width, rect.height);
      this.ctx.fillStyle = styles.getPropertyValue('--cp-scratch-panel').trim() || '#173f2b';
      this.ctx.font = `600 14px ${getComputedStyle(document.body).fontFamily}`;
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(this.canvas.dataset.instruction || 'Scratch here', rect.width / 2, rect.height / 2);
      this.ctx.globalCompositeOperation = 'destination-out';
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.lineWidth = Math.max(28, Math.min(rect.width, rect.height) * 0.18);
    }

    pointFromEvent(event) {
      const rect = this.canvas.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    startScratch(event) {
      if (this.classList.contains('is-revealed')) return;
      this.canvas.setPointerCapture?.(event.pointerId);
      this.isDrawing = true;
      this.lastPoint = this.pointFromEvent(event);
      this.scratchDot(this.lastPoint);
    }

    moveScratch(event) {
      if (!this.isDrawing || !this.ctx) return;
      const point = this.pointFromEvent(event);
      this.ctx.beginPath();
      this.ctx.moveTo(this.lastPoint.x, this.lastPoint.y);
      this.ctx.lineTo(point.x, point.y);
      this.ctx.stroke();
      this.lastPoint = point;
    }

    scratchDot(point) {
      if (!this.ctx) return;
      this.ctx.beginPath();
      this.ctx.arc(point.x, point.y, this.ctx.lineWidth / 2, 0, Math.PI * 2);
      this.ctx.fill();
    }

    endScratch() {
      if (!this.isDrawing) return;
      this.isDrawing = false;
      this.lastPoint = null;
      if (this.scratchedEnough()) this.reveal();
    }

    scratchedEnough() {
      if (!this.ctx) return false;
      const width = this.canvas.width;
      const height = this.canvas.height;
      const data = this.ctx.getImageData(0, 0, width, height).data;
      const step = Math.max(8, Math.floor(Math.min(width, height) / 24));
      let transparent = 0;
      let sampled = 0;
      for (let y = 0; y < height; y += step) {
        for (let x = 0; x < width; x += step) {
          const alpha = data[(y * width + x) * 4 + 3];
          sampled += 1;
          if (alpha < 64) transparent += 1;
        }
      }
      return sampled > 0 && transparent / sampled >= this.threshold;
    }

    reveal() {
      this.classList.add('is-revealed');
      requestAnimationFrame(() => {
        if (this.emailInput) this.emailInput.focus({ preventScroll: true });
        else this.claimButton?.focus({ preventScroll: true });
      });
    }
  }

  customElements.define('cp-scratch-offer', CPScratchOffer);
}
